/**
 * Plan hochladen und Maßstab setzen (10-web-admin § 6.2).
 *
 * The two steps before the editor, laid out as the board draws them: the
 * stepper in the bar, a 320 px column of cards — Planbild, Maßstab, Raster —
 * and the measuring surface with its toolbar. The client checks type and size
 * before sending, but the server checks the bytes again — a limit the client
 * could have honoured is not a check.
 *
 * Both halves of this screen used to end in a shrug. The upload stored bytes in
 * the bucket and nothing wrote the key onto a version, so a plan stayed
 * "Entwurf, kein Plan" however often staff uploaded one; and "Weiter zu
 * Plätzen" navigated away without saving the scale that had just been measured.
 * The server binds the image now, and the scale is saved here.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, LoadingScreen } from '@cowoo/shared/ui-web';
import {
  ApiProblem,
  formatMeasurement,
  GRID_SIZES_CM,
  parseMetres,
  pixelDistance,
  scaleFrom,
  type Point,
} from '@cowoo/shared';
import { api, postMultipart } from '../../lib/api';
import { Dialog, EditorBar, Icon, SwitchRow, fieldClass } from '../../ui';
import { useEditorStore } from './editorStore';

/** D-100 — the same cap the server enforces, checked here to fail fast. */
const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPTED = ['application/pdf', 'image/png', 'image/jpeg', 'image/svg+xml'];

interface PlanImage {
  key: string;
  widthPx: number;
  heightPx: number;
  /** Signed and short-lived — the picture the reference line is drawn on. */
  url: string;
  /** The uploaded file's name and size, where a file was just uploaded. */
  fileName: string | null;
  bytes: number | null;
  /**
   * Uploaded in this sitting, as opposed to the one the plan already had.
   *
   * It decides whether a measurement is required: a new picture invalidates
   * the scale that was measured on the old one, while opening the screen on
   * the existing plan must not demand that a working scale be measured again
   * before anything else can be done.
   */
  fresh: boolean;
}

export function Upload() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { planId = '' } = useParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const snap = useEditorStore((state) => state.snap);
  const setSnap = useEditorStore((state) => state.setSnap);

  const [image, setImage] = useState<PlanImage | null>(null);
  const [opacity, setOpacity] = useState(0.6);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const [points, setPoints] = useState<Point[]>([]);
  const [realLength, setRealLength] = useState('');
  const [gridCm, setGridCm] = useState(25);
  const [applied, setApplied] = useState<number | null>(null);
  /** § 6.2 — a new scale over existing objects is confirmed first. */
  const [confirmScale, setConfirmScale] = useState<number | null>(null);

  /**
   * The draft this upload is for, with whatever is already on it.
   *
   * Opening on the dropzone is right for a new plan and wrong for "Planbild
   * ersetzen": there the screen has to show the picture it is about to
   * replace, and it must not lose the desks that are standing on it when the
   * new scale is saved.
   */
  const draft = useQuery({
    queryKey: ['admin', 'plan-draft', planId],
    enabled: planId !== '',
    queryFn: async () => {
      const created = await api.POST('/admin/floor-plans/{id}/draft', {
        params: { path: { id: planId } },
      });
      if (created.error) throw created.error;
      const contents = await api.GET('/admin/floor-plan-versions/{vid}/preview', {
        params: { path: { vid: created.data.id } },
      });
      if (contents.error) throw contents.error;
      return { ...created.data, plan: contents.data };
    },
  });

  const plans = useQuery({
    queryKey: ['admin', 'floor-plans'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plans');
      if (error) throw error;
      return data.items;
    },
    select: (items) => items.find((item) => item.id === planId) ?? null,
  });

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations', {});
      if (error) throw error;
      return data.items;
    },
  });

  useEffect(() => {
    const plan = draft.data?.plan;
    if (!plan || image !== null) return;
    setGridCm(plan.gridCm);
    setOpacity(plan.backgroundOpacity);
    if (!plan.imageUrl) return;
    setImage({
      key: '',
      widthPx: plan.imageWidthPx ?? 0,
      heightPx: plan.imageHeightPx ?? 0,
      url: plan.imageUrl,
      fileName: null,
      bytes: null,
      fresh: false,
    });
  }, [draft.data, image]);

  const upload = async (file: File): Promise<void> => {
    // Fail before the network on the two things we can already see.
    if (!ACCEPTED.includes(file.type)) return setError(t('admin.upload.wrongType'));
    if (file.size > MAX_BYTES) return setError(t('admin.upload.tooLarge'));

    setError(null);
    setBusy(true);
    const body = new FormData();
    body.append('floorPlanId', planId);
    body.append('file', file);

    const { data, status } = await postMultipart<Omit<PlanImage, 'fresh' | 'fileName' | 'bytes'>>(
      '/admin/uploads/plan-image',
      body,
    );
    setBusy(false);
    if (!data) {
      // The server checks the bytes, so it is the one that knows why.
      return setError(t(status === 413 ? 'admin.upload.tooLarge' : 'errors.validation_failed'));
    }
    setImage({ ...data, fileName: file.name, bytes: file.size, fresh: true });
    setPoints([]);
    setApplied(null);
  };

  const metres = parseMetres(realLength);
  const measuredPx = points.length === 2 ? pixelDistance(points[0]!, points[1]!) : 0;
  const scale = points.length === 2 && metres !== null ? scaleFrom(points[0]!, points[1]!, metres) : null;
  const pxPerCm = applied ?? draft.data?.plan.pxPerCm ?? 1;

  /**
   * "Weiter zu Plätzen" saves what this screen decided — the scale, the grid,
   * the opacity — with the document the draft already carries: the PUT is
   * the whole version, so sending an empty `placements[]` from here would
   * clear the floor.
   */
  const save = useMutation({
    mutationFn: async () => {
      const version = draft.data;
      const locationId = plans.data?.locationId;
      if (!version || !locationId) return;
      const { error } = await api.PUT('/admin/floor-plan-versions/{vid}', {
        params: { path: { vid: version.id } },
        body: {
          rev: version.rev,
          locationId,
          pxPerCm,
          gridCm,
          backgroundOpacity: opacity,
          placements: version.plan.placements.map((placement) => ({
            resourceId: placement.resourceId,
            kind: placement.kind as 'desk',
            label: placement.label,
            zone: placement.zone,
            xCm: placement.xCm,
            yCm: placement.yCm,
            wCm: placement.wCm,
            hCm: placement.hCm,
            rotationDeg: placement.rotationDeg,
            deskType: placement.deskType,
            status: placement.status,
            attributes: placement.attributes,
            capacity: placement.capacity,
            bookableBy: placement.bookableBy,
            maxAdvanceDays: placement.maxAdvanceDays as 14,
          })),
        },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      // The editor reads the same query: without this it opened on the draft
      // as it was before the picture and the scale, and kept it — a plan drawn
      // at the old scale and a `rev` that was stale at the first autosave.
      await queryClient.invalidateQueries({ queryKey: ['admin', 'plan-draft', planId] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'floor-plans'] });
      void navigate(`/grundrisse/${planId}/editor`);
    },
    // 04 § 10 — the server's code names the reason; a stale draft (409) or a
    // refused placement (422) must not both read as "prüfe deine Eingaben".
    onError: (error) => setError(t(ApiProblem.fromThrown(error).i18nKey)),
  });

  if (draft.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const locationName = locations.data?.find((item) => item.id === plans.data?.locationId)?.name ?? '';
  // § 6.2 — a new picture has to be measured, because the scale that was
  // measured on the old one no longer describes it.
  const needsScale = image?.fresh === true && applied === null;
  const step: 1 | 2 = image ? 2 : 1;

  return (
    <div className="bg-fog flex h-[calc(100vh-64px)] flex-col">
      <EditorBar
        onBack={() => void navigate('/grundrisse')}
        title={t('admin.plans.cardName', { location: locationName, floor: plans.data?.name ?? '' })}
        subline={plans.data?.published ? t('admin.upload.sublineExisting') : t('admin.upload.sublineNew')}
        middle={<Stepper current={step} />}
        actions={
          <>
            <Button size="md" variant="secondary" onClick={() => void navigate('/grundrisse')}>
              {t('admin.upload.back')}
            </Button>
            <Button size="md" disabled={needsScale} loading={save.isPending} onClick={() => save.mutate()}>
              {t('admin.upload.next')}
              <Icon name="arrowRight" size={16} />
            </Button>
          </>
        }
      />

      <div className="grid min-h-0 flex-1 grid-cols-[320px_minmax(0,1fr)] gap-6 p-6">
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
          {error ? <Banner tone="danger">{error}</Banner> : null}

          {/* --------------------------- Planbild --------------------------- */}
          <section className="border-line rounded-card flex flex-col gap-3 border bg-white p-[18px]">
            <p className="text-label text-muted uppercase">{t('admin.upload.resultTitle')}</p>
            {image ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="bg-teal-tint text-teal-dark rounded-input flex h-11 w-11 flex-none items-center justify-center">
                    <Icon name="image" size={20} />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="text-adminBody text-ink truncate font-semibold">
                      {image.fileName ?? t('admin.upload.currentImage')}
                    </p>
                    <p className="text-muted text-xs">
                      {t('admin.upload.resultMeta', { width: image.widthPx, height: image.heightPx })}
                      {image.bytes !== null ? ` · ${formatBytes(image.bytes)}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => inputRef.current?.click()}
                  >
                    {t('admin.upload.replace')}
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-adminSmall text-muted">{t('admin.plans.stateNoImageBody')}</p>
            )}
            <div className="flex flex-col gap-1.5">
              <div className="text-ink flex justify-between text-[13px]">
                <span className="font-semibold">{t('admin.upload.opacity')}</span>
                <span className="text-muted tabular-nums">{`${Math.round(opacity * 100)} %`}</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(opacity * 100)}
                onChange={(event) => setOpacity(Number(event.target.value) / 100)}
                aria-label={t('admin.upload.opacity')}
                className="accent-teal w-full"
              />
            </div>
          </section>

          {/* ---------------------------- Maßstab --------------------------- */}
          <section className="border-teal rounded-card flex flex-col gap-3 border-2 bg-white p-[18px]">
            <p className="text-label text-teal uppercase">{t('admin.upload.scaleStep')}</p>
            <p className="text-adminBody text-text leading-normal">{t('admin.upload.scaleBody')}</p>
            <div className="flex flex-col gap-1.5">
              <span className="text-ink text-[13px] font-semibold">{t('admin.upload.measured')}</span>
              <div className={`${fieldClass} flex items-center justify-between`}>
                <span className="tabular-nums">{measuredPx > 0 ? `${Math.round(measuredPx)} px` : '—'}</span>
                <span className="text-muted text-xs">{t('admin.upload.onImage')}</span>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="real-length" className="text-ink text-[13px] font-semibold">
                {t('admin.upload.realLength')}
              </label>
              <div className={`${fieldClass} border-teal flex items-center gap-2 border-2`}>
                <input
                  id="real-length"
                  value={realLength}
                  inputMode="decimal"
                  onChange={(event) => setRealLength(event.target.value)}
                  className="min-w-0 flex-1 bg-transparent tabular-nums outline-none"
                />
                <span className="text-muted text-[13px]">{t('admin.upload.metres')}</span>
              </div>
            </div>
            {scale ? (
              <p className="bg-fog rounded-input text-text flex items-center gap-2 px-3 py-2.5 text-[13px]">
                <Icon name="check" size={16} className="text-teal" />
                {t('admin.upload.scaleHint', {
                  cm: (1 / scale.pxPerCm).toFixed(2).replace('.', ','),
                  px: scale.deskWidthPx,
                })}
              </p>
            ) : null}
            <Button
              size="md"
              disabled={scale === null}
              onClick={() => {
                if (!scale) return;
                // Objects keep their centimetres and move on the picture; with
                // desks on the draft that is worth a look before it happens.
                const hasObjects = (draft.data?.plan.placements.length ?? 0) > 0;
                const hadScale = (draft.data?.plan.pxPerCm ?? null) !== null;
                if (hasObjects && hadScale && scale.pxPerCm !== draft.data?.plan.pxPerCm) {
                  setConfirmScale(scale.pxPerCm);
                } else {
                  setApplied(scale.pxPerCm);
                }
              }}
            >
              {applied !== null && scale?.pxPerCm === applied
                ? t('admin.upload.scaleApplied')
                : t('admin.upload.applyScale')}
            </Button>
            {/* § 6.2 — an image without a scale may still go on as a blank grid. */}
            {image === null ? (
              <Button size="sm" variant="ghost" onClick={() => save.mutate()}>
                {t('admin.upload.withoutImage')}
              </Button>
            ) : null}
          </section>

          {/* ----------------------------- Raster --------------------------- */}
          <section className="border-line rounded-card flex flex-col gap-2 border bg-white p-[18px]">
            <p className="text-label text-muted uppercase">{t('admin.upload.gridTitle')}</p>
            <SwitchRow label={t('admin.upload.gridSnap')} checked={snap} onChange={setSnap} />
            <div className="text-adminBody text-ink flex items-center justify-between">
              <span>{t('admin.upload.gridSize')}</span>
              <select
                value={gridCm}
                onChange={(event) => setGridCm(Number(event.target.value))}
                aria-label={t('admin.upload.gridSize')}
                className="border-line-strong rounded-input h-[34px] w-[100px] border bg-white px-2.5 text-[13px] tabular-nums"
              >
                {GRID_SIZES_CM.map((size) => (
                  <option key={size} value={size}>{`${size} cm`}</option>
                ))}
              </select>
            </div>
          </section>
        </div>

        {/* ------------------------------ surface ----------------------------- */}
        <section className="border-line rounded-card relative flex min-h-0 flex-col overflow-hidden border bg-white">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED.join(',')}
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          {image ? (
            <>
              <div className="border-line text-muted flex h-11 flex-none items-center justify-between border-b px-4 text-[13px]">
                <div className="flex items-center gap-2">
                  <span className="bg-teal-tint text-teal-dark inline-flex h-[30px] items-center gap-1.5 rounded-full px-2.5 font-semibold">
                    <Icon name="desk" size={14} />
                    {t('admin.upload.measuringChip')}
                  </span>
                  <span>{t('admin.upload.twoPoints')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tabular-nums">{t('admin.upload.zoom', { zoom: 100 })}</span>
                  <span>{t('admin.upload.escCancels')}</span>
                </div>
              </div>
              <div className="bg-fog flex min-h-0 flex-1 items-center justify-center overflow-auto p-6 [background-image:repeating-linear-gradient(0deg,#f1f4f5_0_1px,transparent_1px_25px),repeating-linear-gradient(90deg,#f1f4f5_0_1px,transparent_1px_25px)]">
                <Measure
                  image={image}
                  opacity={opacity}
                  points={points}
                  onPoint={(point) =>
                    setPoints((current) => (current.length >= 2 ? [point] : [...current, point]))
                  }
                  onCancel={() => setPoints([])}
                  label={metres !== null && measuredPx > 0 ? formatMeasurement(metres, measuredPx) : null}
                />
              </div>
            </>
          ) : (
            <div
              className={`m-6 flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-10 text-center ${
                dragging ? 'border-teal bg-teal-tint' : 'border-line'
              }`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                const file = event.dataTransfer.files[0];
                if (file) void upload(file);
              }}
            >
              <Icon name="image" size={28} className="text-placeholder" />
              <p className="text-ink font-semibold">{t('admin.upload.dropzone')}</p>
              <p className="text-muted text-[13px]">{t('admin.upload.formats')}</p>
              <Button size="md" loading={busy} onClick={() => inputRef.current?.click()}>
                {t('admin.upload.choose')}
              </Button>
              {busy ? <p className="text-muted text-[13px]">{t('admin.upload.converting')}</p> : null}
            </div>
          )}
        </section>
      </div>
      {confirmScale !== null ? (
        <Dialog
          title={t('admin.upload.applyScale')}
          intro={t('admin.upload.scaleConfirm')}
          confirmLabel={t('admin.upload.applyScale')}
          onConfirm={() => {
            setApplied(confirmScale);
            setConfirmScale(null);
          }}
          onClose={() => setConfirmScale(null)}
        />
      ) : null}
    </div>
  );
}

/** § 6.2 — (1) Plan hochladen → (2) Maßstab → (3) Plätze → (4) Veröffentlichen. */
function Stepper({ current }: { current: 1 | 2 }) {
  const { t } = useTranslation();
  const steps = [
    t('admin.upload.step1'),
    t('admin.upload.step2'),
    t('admin.upload.step3'),
    t('admin.upload.step4'),
  ];
  return (
    <ol
      className="text-muted flex items-center gap-3 text-[13px]"
      aria-label={t('admin.upload.step', { current })}
    >
      {steps.map((label, index) => {
        const number = index + 1;
        const done = number < current;
        const active = number === current;
        return (
          <li key={label} className="contents">
            {index > 0 ? <span className="bg-line h-px w-7" aria-hidden /> : null}
            <span
              className={`inline-flex items-center gap-2 ${done ? 'text-teal-dark font-semibold' : ''} ${
                active ? 'text-ink font-semibold' : ''
              }`}
              aria-current={active ? 'step' : undefined}
            >
              <span
                className={`inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs ${
                  done || active ? 'bg-teal text-white' : 'border-line border-2'
                }`}
              >
                {done ? <Icon name="check" size={12} className="[stroke-width:3]" /> : number}
              </span>
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Two clicks set the reference line; a third starts over; `Esc` clears. */
function Measure({
  image,
  opacity,
  points,
  onPoint,
  onCancel,
  label,
}: {
  image: PlanImage;
  opacity: number;
  points: Point[];
  onPoint: (point: Point) => void;
  onCancel: () => void;
  label: string | null;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <figure className="m-0 w-full max-w-[960px]">
      <svg
        data-testid="measure-surface"
        viewBox={`0 0 ${image.widthPx} ${image.heightPx}`}
        className="block w-full cursor-crosshair"
        role="application"
        aria-label={t('admin.upload.measuring')}
        onClick={(event) => {
          const target = event.currentTarget;
          const point = target.createSVGPoint();
          point.x = event.clientX;
          point.y = event.clientY;
          const matrix = target.getScreenCTM()?.inverse();
          const mapped = matrix ? point.matrixTransform(matrix) : point;
          onPoint({ x: mapped.x, y: mapped.y });
        }}
      >
        {/* Without the href this drew nothing at all, so two points were
            clicked onto an empty rectangle and the measurement meant nothing. */}
        <image href={image.url} x={0} y={0} width={image.widthPx} height={image.heightPx} opacity={opacity} />
        {points.length === 2 ? (
          <>
            <line
              x1={points[0]!.x}
              y1={points[0]!.y}
              x2={points[1]!.x}
              y2={points[1]!.y}
              stroke="#d9e900"
              strokeWidth={Math.max(2, image.widthPx / 400)}
            />
            {label ? (
              <text
                x={(points[0]!.x + points[1]!.x) / 2}
                y={(points[0]!.y + points[1]!.y) / 2 - image.widthPx / 100}
                textAnchor="middle"
                fontSize={Math.max(12, image.widthPx / 60)}
                fontFamily="Figtree, sans-serif"
                fontWeight={600}
                fill="#151515"
              >
                {label}
              </text>
            ) : null}
          </>
        ) : null}
        {points.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r={Math.max(4, image.widthPx / 250)}
            fill="#d9e900"
            stroke="#151515"
          />
        ))}
      </svg>
    </figure>
  );
}

function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1
    ? `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(mb)}\u00a0MB`
    : `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(bytes / 1024)}\u00a0KB`;
}
